import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { useCompassCard } from '@scale/compass-ui/store';
import { Code, RadioGroup, Text, Heading, Checkbox } from '@scale/scaleui-radix';
import { MetadataUpdateType } from '@scale/shared/nucleus/types';
import { UpdateMetadataInstructions } from './UpdateMetadataInstructions';
import { TaskIdInfo } from './TaskIdInfo';
import { MetadataPreview } from './MetadataPreview';
import { getTaskIdColumn, getSampleTaskId, getMetadataColumns, getSampleValue } from './utils';
import { Column } from '@scale/compass-shared/types';
import { PreviewMetadataUpdate } from './PreviewMetadataUpdate';
import type { UpdateMetadataContext } from '@scale/genai-compass-shared/cards/update_metadata';

const UpdateMetadataCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const { card, updateCardContext } = useCompassCard<UpdateMetadataContext>(index);
  const context = card.defaultContext;
  const { retrievedOutput } = useCompassCard(index - 1);

  const [selectedColumns, setSelectedColumns] = [
    context.columns,
    (selectedColumns: Column[]) => {
      const newSelectedColumns = metadataColumns.filter((column: Column) =>
        selectedColumns.some(selected => selected.header === column.header),
      );
      updateCardContext({ ...context, columns: newSelectedColumns });
    },
  ];

  const updateType = context.updateType ?? MetadataUpdateType.REPLACE;

  const handleUpdateTypeChange = (value: string) => {
    updateCardContext({
      ...context,
      updateType: value as MetadataUpdateType,
    });
  };

  const restartHumanNodes = context.restartHumanNodes ?? false;

  const handleRestartHumanNodesChange = (checked: boolean) => {
    updateCardContext({
      ...context,
      restartHumanNodes: checked,
    });
  };

  const taskIdColumn = getTaskIdColumn(retrievedOutput);
  const sampleTaskId = getSampleTaskId(retrievedOutput, taskIdColumn);
  const metadataColumns = getMetadataColumns(retrievedOutput);

  const tableData = metadataColumns.map((col: Column) => ({
    header: col.header,
    value:
      getSampleValue(retrievedOutput, col.header) === '<empty>' ? (
        <Code variant="soft" color="neutral">
          {'<empty>'}
        </Code>
      ) : (
        getSampleValue(retrievedOutput, col.header)
      ),
  }));

  return (
    <div className="flex flex-row gap-8">
      <div className="w-1/2">
        <UpdateMetadataInstructions />
      </div>

      <div className="w-1/2">
        <div className="flex flex-col gap-4">
          <TaskIdInfo taskIdColumn={taskIdColumn} />

          <div className="flex flex-col gap-2">
            <Heading size="2" weight="medium" className="text-gray-700">
              Update Mode
            </Heading>
            <RadioGroup.Root
              value={updateType}
              onValueChange={handleUpdateTypeChange}
              className="flex flex-col gap-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroup.Item value={MetadataUpdateType.REPLACE} id="replace" />
                <label htmlFor="replace" className="text-sm">
                  <Text weight="medium">Replace</Text> - Overwrite existing metadata fields
                </label>
              </div>
              {updateType === MetadataUpdateType.REPLACE && (
                <Text size="1" color="accent" mt="1">
                  Fields that are empty will be set as an empty string.
                </Text>
              )}
              <div className="flex items-center space-x-2">
                <RadioGroup.Item value={MetadataUpdateType.MERGE} id="merge" />
                <label htmlFor="merge" className="text-sm">
                  <Text weight="medium">Merge</Text> - Only update fields that don't already exist
                </label>
              </div>
            </RadioGroup.Root>
          </div>

          <div className="flex flex-col gap-2">
            <Heading size="2" weight="medium" className="text-gray-700">
              Advanced Options
            </Heading>
            <Text className="flex flex-row gap-2 items-center">
              <Checkbox
                checked={restartHumanNodes}
                onCheckedChange={handleRestartHumanNodesChange}
              />
              Restart human nodes after metadata update
            </Text>
            {restartHumanNodes && (
              <Text size="1" color="amber">
                This will kick out contributors from tasks. Only recommended if worker
                qualifications or specializations are updated.
              </Text>
            )}
          </div>

          <MetadataPreview
            sampleTaskId={sampleTaskId}
            tableData={tableData}
            isLoading={!retrievedOutput}
            metadataColumns={metadataColumns}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
          />
          <PreviewMetadataUpdate columnsToUpdate={selectedColumns} />
        </div>
      </div>
    </div>
  );
};

UpdateMetadataCardComponent.displayName = 'UpdateMetadataCardComponent';

export default UpdateMetadataCardComponent;
